import {
  type ChangeEvent,
  type ReactNode,
  type FormEvent,
  memo,
  useState,
} from 'react';
import { Button, PasswordInput } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { AppTextInput } from '@/components/common/AppTextInput/AppTextInput';
import splitStyles from '@/components/layout/SplitScreenLayout/SplitScreenLayout.module.css';
import { AvatarUpload } from './AvatarUpload';
import styles from '../InvitationPage.module.css';

interface ProfileStepProps {
  email: string;
}

const MIN_PASSWORD_LENGTH = 8;

export const ProfileStep = memo(function ProfileStep({
  email,
}: ProfileStepProps): ReactNode {
  const { t } = useTranslation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  function handleUsernameChange(event: ChangeEvent<HTMLInputElement>) {
    setUsername(event.target.value);
  }

  function handlePasswordChange(event: ChangeEvent<HTMLInputElement>) {
    setPassword(event.target.value);
  }

  function handleConfirmPasswordChange(event: ChangeEvent<HTMLInputElement>) {
    setConfirmPassword(event.target.value);
  }

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
  }

  const isValid =
    username.trim().length > 0 &&
    password.length >= MIN_PASSWORD_LENGTH &&
    password === confirmPassword;

  return (
    <div className={styles.rightFormWrapper}>
      <form onSubmit={handleSubmit} className={styles.profileForm}>
        <div className={`${splitStyles.fadeElement} ${splitStyles.fadeDelay2}`}>
          <AvatarUpload onFileSelected={() => {}} />
        </div>

        <div className={`${splitStyles.fadeElement} ${splitStyles.fadeDelay3}`}>
          <AppTextInput
            label={t('invitation.profile.username')}
            placeholder={t('invitation.profile.usernamePlaceholder')}
            value={username}
            onChange={handleUsernameChange}
            required
          />
        </div>

        <div className={`${splitStyles.fadeElement} ${splitStyles.fadeDelay4}`}>
          <AppTextInput
            label={t('invitation.profile.email')}
            value={email}
            readOnly
            description={t('invitation.profile.emailHelper')}
          />
        </div>

        <div className={`${splitStyles.fadeElement} ${splitStyles.fadeDelay5}`}>
          <PasswordInput
            label={t('invitation.profile.password')}
            placeholder={t('invitation.profile.passwordPlaceholder')}
            value={password}
            onChange={handlePasswordChange}
            required
            minLength={MIN_PASSWORD_LENGTH}
          />
        </div>

        <div className={`${splitStyles.fadeElement} ${splitStyles.fadeDelay6}`}>
          <PasswordInput
            label={t('invitation.profile.confirmPassword')}
            placeholder={t('invitation.profile.confirmPasswordPlaceholder')}
            value={confirmPassword}
            onChange={handleConfirmPasswordChange}
            required
          />
        </div>

        <div className={`${splitStyles.fadeElement} ${splitStyles.fadeDelay7}`}>
          <Button type="submit" size="md" fullWidth disabled={!isValid}>
            {t('invitation.profile.submit')}
          </Button>
        </div>
      </form>
    </div>
  );
});
